import type { ContentSettings } from "@/lib/utils/content-settings/content-settings-defaults";
import { DEFAULT_CONTENT_SETTINGS } from "@/lib/utils/content-settings/content-settings-defaults";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import { GlobalApi } from "@/openapi/generated";
import type { GetSettingResponse } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";

/** Global settings group for all CMS content strings (microsite + portal). */
export const CONTENT_SETTINGS_GROUP = "CONTENT";

const LEGACY_KEY_PREFIX = "content";

const CONTENT_SETTINGS_KEYS = Object.keys(
  DEFAULT_CONTENT_SETTINGS,
) as (keyof ContentSettings)[];

const LEGACY_FETCH_BATCH_SIZE = 8;
const UPSERT_BATCH_SIZE = 6;

export type TenantContentSettingsResult = {
  settings: ContentSettings;
  persistedKeys: ReadonlySet<keyof ContentSettings>;
};

const EMPTY_PERSISTED_KEYS = new Set<keyof ContentSettings>();

export function mergeContentSettings(
  source: Partial<ContentSettings> | null | undefined,
): ContentSettings {
  if (!source) {
    return { ...DEFAULT_CONTENT_SETTINGS };
  }

  const merged = { ...DEFAULT_CONTENT_SETTINGS };
  for (const key of CONTENT_SETTINGS_KEYS) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      merged[key] = value;
    }
  }
  return merged;
}

function createGlobalApi(accessToken?: string): GlobalApi {
  if (accessToken) {
    return createAuthenticatedApi(GlobalApi, accessToken);
  }
  return new GlobalApi(createOpenApiConfiguration());
}

function isNotFoundOrBadRequest(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 400)
  );
}

export function normalizeContentSettingKey(
  rawKey: string,
): keyof ContentSettings | null {
  const key = rawKey.startsWith(`${LEGACY_KEY_PREFIX}.`)
    ? rawKey.slice(LEGACY_KEY_PREFIX.length + 1)
    : rawKey;

  if (CONTENT_SETTINGS_KEYS.includes(key as keyof ContentSettings)) {
    return key as keyof ContentSettings;
  }

  return null;
}

export function mapContentSettingRows(
  rows: Pick<GetSettingResponse, "key" | "value">[],
): Partial<ContentSettings> {
  const partial: Partial<ContentSettings> = {};

  for (const row of rows) {
    const field = normalizeContentSettingKey(row.key);
    if (field && typeof row.value === "string") {
      partial[field] = row.value;
    }
  }

  return partial;
}

export function getChangedContentSettingFields(
  baseline: ContentSettings,
  next: ContentSettings,
): (keyof ContentSettings)[] {
  return CONTENT_SETTINGS_KEYS.filter((field) => baseline[field] !== next[field]);
}

type GroupFetchResult = {
  partial: Partial<ContentSettings>;
  /** True when by-group responded successfully (including an empty list). */
  succeeded: boolean;
};

async function fetchGlobalSettingsByGroup(
  tenantId: number,
  accessToken?: string,
): Promise<GroupFetchResult> {
  const api = createGlobalApi(accessToken);

  try {
    const rows = await api.apiGlobalSettingsByGroupGet({
      group: CONTENT_SETTINGS_GROUP,
      xTenantID: tenantId,
    });
    return { partial: mapContentSettingRows(rows), succeeded: true };
  } catch (error) {
    if (isNotFoundOrBadRequest(error)) {
      return { partial: {}, succeeded: false };
    }
    throw error;
  }
}

async function fetchLegacyContentSetting(
  tenantId: number,
  field: keyof ContentSettings,
  accessToken?: string,
): Promise<string | null> {
  const api = createGlobalApi(accessToken);
  const tryKeys = [field, `${LEGACY_KEY_PREFIX}.${field}`];

  for (const key of tryKeys) {
    try {
      const row = await api.apiGlobalSettingsByKeyGet({
        key,
        xTenantID: tenantId,
      });
      if (typeof row?.value === "string") {
        return row.value;
      }
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === 404) {
        continue;
      }
      return null;
    }
  }

  return null;
}

async function fetchMissingLegacyContentSettings(
  tenantId: number,
  existing: Partial<ContentSettings>,
  accessToken?: string,
): Promise<Partial<ContentSettings>> {
  const missingKeys = CONTENT_SETTINGS_KEYS.filter((key) => !(key in existing));
  if (missingKeys.length === 0) {
    return {};
  }

  const partial: Partial<ContentSettings> = {};

  for (let i = 0; i < missingKeys.length; i += LEGACY_FETCH_BATCH_SIZE) {
    const batch = missingKeys.slice(i, i + LEGACY_FETCH_BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async (field) => {
        const value = await fetchLegacyContentSetting(
          tenantId,
          field,
          accessToken,
        );
        return { field, value };
      }),
    );

    for (const { field, value } of results) {
      if (typeof value === "string" && value.trim().length > 0) {
        partial[field] = value;
      }
    }
  }

  return partial;
}

export async function fetchTenantContentSettings(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<TenantContentSettingsResult> {
  try {
    const { partial: fromGroup, succeeded: groupFetchSucceeded } =
      await fetchGlobalSettingsByGroup(tenantId, options?.accessToken);

    const fromLegacy = groupFetchSucceeded
      ? {}
      : await fetchMissingLegacyContentSettings(
          tenantId,
          fromGroup,
          options?.accessToken,
        );

    const persistedPartial = { ...fromGroup, ...fromLegacy };
    const persistedKeys = new Set(
      Object.keys(persistedPartial) as (keyof ContentSettings)[],
    );

    return {
      settings: mergeContentSettings(persistedPartial),
      persistedKeys,
    };
  } catch {
    return {
      settings: { ...DEFAULT_CONTENT_SETTINGS },
      persistedKeys: EMPTY_PERSISTED_KEYS,
    };
  }
}

async function throwContentSettingError(
  error: unknown,
  field: keyof ContentSettings,
  action: "create" | "update",
): Promise<never> {
  if (
    action === "create" &&
    error instanceof ResponseError &&
    error.response.status === 404
  ) {
    throw new Error(
      "POST /api/global-settings is not available on the server (404). " +
        "Backend must enable the create endpoint before new content settings can be saved.",
    );
  }

  const message = await getApiErrorMessage(
    error,
    `Failed to ${action} content setting "${field}"`,
  );
  throw new Error(message);
}

async function upsertContentSetting(
  tenantId: number,
  field: keyof ContentSettings,
  value: string,
  accessToken: string,
  existsInDatabase: boolean,
): Promise<void> {
  const api = createAuthenticatedApi(GlobalApi, accessToken);
  const createRequest = {
    xTenantID: tenantId,
    createSettingRequest: {
      key: field,
      value,
      group: CONTENT_SETTINGS_GROUP,
    },
  };
  const updateRequest = {
    xTenantID: tenantId,
    updateSettingRequest: {
      key: field,
      value,
      group: CONTENT_SETTINGS_GROUP,
    },
  };

  if (existsInDatabase) {
    try {
      await api.apiGlobalSettingsPut(updateRequest);
      return;
    } catch (error) {
      if (!isNotFoundOrBadRequest(error)) {
        await throwContentSettingError(error, field, "update");
      }
    }
  }

  try {
    await api.apiGlobalSettingsPost(createRequest);
    return;
  } catch (error) {
    if (!(error instanceof ResponseError && error.response.status === 409)) {
      await throwContentSettingError(error, field, "create");
    }
  }

  try {
    await api.apiGlobalSettingsPut(updateRequest);
  } catch (error) {
    await throwContentSettingError(error, field, "update");
  }
}

export async function updateTenantContentSettings(
  tenantId: number,
  settings: ContentSettings,
  accessToken: string,
  options: {
    fieldsToUpdate: (keyof ContentSettings)[];
    persistedKeys: ReadonlySet<keyof ContentSettings>;
  },
): Promise<ContentSettings> {
  const { fieldsToUpdate, persistedKeys } = options;

  if (fieldsToUpdate.length === 0) {
    return mergeContentSettings(settings);
  }

  const failures: string[] = [];

  for (let i = 0; i < fieldsToUpdate.length; i += UPSERT_BATCH_SIZE) {
    const batch = fieldsToUpdate.slice(i, i + UPSERT_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map((field) =>
        upsertContentSetting(
          tenantId,
          field,
          settings[field],
          accessToken,
          persistedKeys.has(field),
        ),
      ),
    );

    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const field = batch[index];
        const reason =
          result.reason instanceof Error
            ? result.reason.message
            : "Unknown error";
        failures.push(`${field}: ${reason}`);
      }
    });
  }

  if (failures.length > 0) {
    throw new Error(
      failures.length === 1
        ? failures[0]
        : `Failed to save ${failures.length} content settings`,
    );
  }

  return mergeContentSettings(settings);
}
